<template>
    <v-dialog v-model="show_dialog" :persistent="true" max-width="1264px" scrollable>
        <v-card class="custom_card">

            <v-card-title>
                <span class="headline">{{header}}</span>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text id="manage_sale_payment_page">
                <v-row dense>
                    <v-col cols="12">
                        <div class="title mb-4">{{$t('sale_payments.data')}}</div>
                    </v-col>
                    <v-col cols="12" sm="6" md="6" lg="3">
                        <v-menu v-model="date_menu" :close-on-content-click="false"
                                max-width="290">
                            <template v-slot:activator="{ on }">
                                <v-text-field name="date" type="text" class="custom_field"
                                              :value="$Helper.methods.formatDate(item.date)"
                                              :label="$t('sale_payments.date')" outlined readonly
                                              :disabled="isFieldsDisabled" v-on="on" @blur="$v.item.date.$touch()"
                                              @input="dateFieldCleared('date')">
                                </v-text-field>
                            </template>
                            <v-date-picker v-model="item.date" no-title @change="date_menu = false">
                            </v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col cols="12" sm="6" md="6" lg="3">
                        <v-text-field name="amount" type="text" class="custom_field" v-model="item.amount"
                                      :label="$t('general.amount')" outlined clearable :disabled="isFieldsDisabled"
                                      @input="$v.item.amount.$touch()" @blur="$v.item.amount.$touch()"
                                      :error-messages="fieldErrors('amount')">
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="6" lg="3">
                        <v-select name="payment_type_id" class="custom_field" v-model="item.payment_type_id"
                                  :items="payment_types" :label="$t('general.payment_type')" item-text="name"
                                  item-value="id" outlined clearable :disabled="isFieldsDisabled"
                                  @input="$v.item.payment_type_id.$touch()" @blur="$v.item.payment_type_id.$touch()"
                                  :error-messages="fieldErrors('payment_type_id')">
                        </v-select>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="elevation-1">
                <v-spacer></v-spacer>
                <v-btn color="primary" class="close_button" dark @click="closeDialog">
                    {{$t('general.close')}}
                </v-btn>
                <v-btn v-if="action === 'view'" color="primary" class="update_button" dark @click="updateItem">
                    {{$t('sale_payments.update')}}
                </v-btn>
                <v-btn v-if="action !== 'view'" class="primary submit_button" dark @click="submitDialog()">
                    {{$t('general.save')}}
                </v-btn>
            </v-card-actions>

        </v-card>
    </v-dialog>
</template>

<script>
    import {validationMixin} from 'vuelidate'
    import {required, decimal, maxValue, minValue} from 'vuelidate/lib/validators'

    export default {
        props: {
            show: {type: Boolean, default: false},
            item_header: {type: String},
            item_action: {type: String},
            item_data: {type: Object}
        },
        mixins: [validationMixin],
        validations() {
            return {
                item: {
                    date: {required},
                    amount: {
                        required,
                        decimal,
                        minValue: value => value > 0,
                        maxValue: value => {

                            if (parseInt(this.$store.state.payment_price) === 0) {
                                return value <= Number.Infinity;
                            }

                            // create
                            if (this.item_action === 'create') {
                                return value <= this.$store.state.remaining_balance;

                                // update
                            } else {
                                return value <= parseInt(this.$store.state.remaining_balance) + parseInt(this.item_data.amount);
                            }
                        },
                    },
                    payment_type_id: {required}
                }
            }
        },
        data: self => ({
            show_dialog: self.show,
            action: self.item_action,
            header: self.item_header,

            // look up data
            payment_types: [],

            // data
            date_menu: false,

            // item
            item: {
                id: null,
                date: null,
                amount: null,
                payment_type_id: null,
                payment_type_name: null
            }
        }),
        computed: {
            isFieldsDisabled() {
                return this.action === 'view';
            },
            payment_type() {
                return this.item.payment_type_id;
            }
        },
        async mounted() {
            // get payment types
            let payment_types = await this.$store.dispatch('apiRequest', {
                method: 'get',
                url: 'settings/payment_types'
            });
            this.payment_types = this.$Helper.methods.sortArrayOfObjects({array: payment_types.data, property: 'name'});
        },
        watch: {
            async show(val) {
                this.show_dialog = val;

                if (val) {
                    this.action = this.item_action;

                    // create
                    if (this.action === 'create') {
                        this.item.date = new Date().toISOString().substr(0, 10);
                        this.item.payment_type_id = 1;

                        // view, update
                    } else {
                        this.$nextTick().then(() => {
                            for (let prop in this.item) {
                                this.item[prop] = this.item_data[prop];
                            }
                        });
                    }
                }
            },
            item_action(val) {
                this.action = val;
            },
            item_header(val) {
                this.header = val;
            },
            payment_type(val) {
                if (val) {
                    this.item.payment_type_name = this.payment_types.find(obj => obj.id === val).name;
                }
            }
        },
        methods: {
            // form field errors
            fieldErrors(prop) {
                const errors = [];
                if (!this.$v.item[prop].$dirty) return errors;

                if (['amount'].indexOf(prop) > -1) {
                    !this.$v.item[prop].decimal && errors.push(this.$t('validation.numeric'));
                    !this.$v.item[prop].minValue && errors.push(this.$t('validation.positive_number'));
                    !this.$v.item[prop].maxValue && errors.push(this.$t('validation.not_greater_than_balance'));
                }

                !this.$v.item[prop].required && errors.push(this.$t('validation.required'));
                return errors;
            },
            // clear dialog
            clearDialog() {
                this.$v.item.$reset();

                for (let prop in this.item) {
                    this.item[prop] = null;
                }
            },
            // trigger close dialog event in parent component
            async closeDialog() {
                this.clearDialog();
                this.$emit('close-dialog');
            },
            // trigger submit dialog event in parent component
            async submitDialog() {
                this.$v.item.$touch();

                // item is valid
                if (!this.$v.item.$invalid) {
                    this.$emit('submit-dialog', Object.assign({}, this.item), this.action);
                    this.clearDialog();
                }
            },
            // change view to update
            updateItem() {
                this.action = 'update';
                this.header = this.$t('sale_payments.update');
            }
        }
    }
</script>